import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Globe,
  Zap,
  Target,
  Users,
  ArrowRight,
  Activity,
  Cpu,
  Database,
  Lock,
  BarChart,
  Server,
  Stethoscope,
  Settings,
  Clock,
  CheckCircle,
  Network,
  Smartphone,
  Wifi
} from "lucide-react";

import Navigation from "../components/Navigation";
import FooterSection from "../components/FooterSection";
import "./About.css";

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  const stats = [
    { label: "Active Users", value: "25,000+", icon: Users },
    { label: "Data Accuracy", value: "99.9%", icon: Activity },
    { label: "System Uptime", value: "24/7", icon: Clock },
    { label: "Safe Storage", value: "Encrypted", icon: Lock }
  ];

  const simpleFeatures = [
    {
      title: "Easy Tracking",
      icon: Activity,
      desc: "Watch your health vitals like heart rate and oxygen in real-time without any messy paperwork."
    },
    {
      title: "Fast Help",
      icon: Zap,
      desc: "Get instant alerts if something feels wrong, so your doctor can help you faster than ever."
    },
    {
      title: "Secure Data",
      icon: ShieldCheck,
      desc: "Your medical history is locked tight with high-level digital security, just for you and your doctor."
    },
    {
      title: "Mobile Friendly",
      icon: Smartphone,
      desc: "Checking your health is as easy as checking your messages. Access Medico from anywhere."
    }
  ];

  return (
    <div className="abt-page-shell">
      <Navigation />

      {/* Background Effects */}
      <div className="abt-tactical-grid"></div>
      <div className="abt-scanline-overlay"></div>

      <motion.main
        className="abt-content-hub"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        
        {/* HERO SECTION - Simple Greeting */}
        <section className="abt-hero-sector">
          <div className="abt-hud-badge">
            <Activity size={14} />
            <span>LEARN ABOUT MEDICO</span>
          </div>

          <h1 className="abt-hero-title">
            Simple Healthcare <span className="abt-highlight">For Everyone</span>
          </h1>

          <p className="abt-hero-subtitle">
            Medico.in is a smart platform that connects patients, doctors, and caregivers. 
            We use simple technology to make healthcare faster, safer, and easier for you.
          </p>

          <div className="abt-hero-actions">
            <Link to="/auth" className="abt-btn abt-primary">
              Join Us Now <ArrowRight size={18} />
            </Link>
            <Link to="/ai" className="abt-btn abt-secondary">
              Talk to AI
            </Link>
          </div>
        </section>

        {/* MISSION SECTION - Why we do it */}
        <section className="abt-dual-node-sector">
          <div className="abt-sector-grid">
            <div className="abt-vision-card abt-glass">
              <Target size={32} />
              <h3>The Mission</h3>
              <p>
                Our goal is to make sure nobody has to wait for basic health info. 
                We provide tools that show your data instantly, so you can stay healthy and happy.
              </p>
            </div>

            <div className="abt-vision-card abt-glass">
              <Globe size={32} />
              <h3>Our Promise</h3>
              <p>
                We promise to keep your data private and our system running 24/7. 
                No matter where you are, Medico is there to help monitor your health.
              </p>
            </div>
          </div>
        </section>

        {/* FEATURES - What can you do? */}
        <section className="abt-ops-sector">
          <h2 className="abt-section-title">What We Offer</h2>

          <div className="abt-feature-grid">
            {simpleFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="abt-op-card abt-glass">
                  <Icon size={40} />
                  <h4>{f.title}</h4>
                  <p>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* SECURITY - Simple trust message */}
        <section className="abt-security-sector">
          <div className="abt-security-visual abt-glass">
            <ShieldCheck size={48} />
            <h2>Your Safety is Our Priority</h2>
            <p>
              We know your health data is personal. That's why we use the same kind of 
              protection that banks use. You are in control of who sees your profile.
            </p>

            <div className="abt-security-stack">
              <div className="abt-stack-item"><CheckCircle size={16} /> Private Protected Login</div>
              <div className="abt-stack-item"><CheckCircle size={16} /> 100% Secure Storage</div>
              <div className="abt-stack-item"><CheckCircle size={16} /> Verified Doctor Network</div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS - New Simple Content */}
        <section className="abt-ops-sector">
          <h2 className="abt-section-title">How It Works</h2>
          <div className="abt-feature-grid">
            <div className="abt-op-card abt-glass">
              <Wifi size={32} />
              <h4>1. Connect</h4>
              <p>Hook up your smart health devices easily with one click.</p>
            </div>
            <div className="abt-op-card abt-glass">
              <Activity size={32} />
              <h4>2. Monitor</h4>
              <p>Watch your numbers move on your live dashboard anytime.</p>
            </div>
            <div className="abt-op-card abt-glass">
              <Stethoscope size={32} />
              <h4>3. Consult</h4>
              <p>Share your reports with your doctor instantly and get advice.</p>
            </div>
          </div>
        </section>

        {/* CLINICAL PERSONAS */}
        <section className="abt-audience-sector">
          <motion.div className="abt-section-header center" variants={itemVariants}>
            <h2 className="abt-section-title">CLINICAL_PERSONAS</h2>
          </motion.div>
          <div className="abt-audience-wheel">
            {[
              { role: "Patients", icon: <Activity />, color: "cyan" },
              { role: "Caregivers", icon: <Users />, color: "blue" },
              { role: "Doctors", icon: <Stethoscope />, color: "green" },
              { role: "Admins", icon: <Settings />, color: "purple" }
            ].map((persona, idx) => (
              <motion.div key={idx} className={`abt-persona-node ${persona.color}`} variants={itemVariants}>
                <div className="abt-persona-icon">{persona.icon}</div>
                <span>{persona.role}</span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* STATS - Quick Numbers */}
        <section className="abt-stats-sector">
          <div className="abt-stats-grid">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className="abt-stat-card abt-glass">
                  <Icon size={24} />
                  <div>{stat.value}</div>
                  <div style={{fontSize: '0.8rem', color: '#94a3b8'}}>{stat.label}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA - Final invitation */}
        <section className="abt-cta-sector">
          <div className="abt-cta-container abt-glass">
            <h2>Ready to Take Control?</h2>
            <p>Joining Medico takes less than a minute. Start your healthy journey today.</p>

            <div className="abt-cta-btns">
              <Link to="/auth" className="abt-btn abt-primary">
                Create Free Account
              </Link>
              <Link to="/contact" className="abt-btn abt-secondary">
                Email Support
              </Link>
            </div>
          </div>
        </section>
      </motion.main>

      <FooterSection />
    </div>
  );
};

export default About;